/**
 * Display formatters for calendar UI. Imperial units (miles / yards /
 * inches / mph / lbf) — function names kept generic so call sites
 * don't need to know which unit system is active.
 *
 * Conversion constants (CODATA-derived, rounded for display):
 *   1 km = 0.6213712 mi
 *   1 m  = 1.0936133 yd
 *   1 m  = 39.3700787 in
 *   1 m/s = 2.2369363 mph
 *   1 N  = 0.2248089 lbf
 *   1 cm = 0.3937008 in
 */

const KM_TO_MI  = 0.6213712;
const M_TO_YD   = 1.0936133;
const M_TO_FT   = 3.2808399;
const M_TO_IN   = 39.3700787;
const MS_TO_MPH = 2.2369363;
const N_TO_LBF  = 0.2248089;

export function fmtKm(m: number | null | undefined): string | null {
  if (m == null || m <= 0) return null;
  const mi = (m / 1000) * KM_TO_MI;
  return `${mi.toFixed(1)} mi`;
}

export function fmtMin(min: number | null | undefined): string | null {
  if (min == null || min <= 0) return null;
  return `${Math.round(min)} min`;
}

export function fmtInt(n: number | null | undefined): string | null {
  if (n == null) return null;
  return n.toLocaleString();
}

/** Used for jump height: meters → inches (HawkinTest stores in m). */
export function fmtCm(m: number | null | undefined): string | null {
  if (m == null) return null;
  return `${(m * M_TO_IN).toFixed(1)} in`;
}

/** Newtons → pound-force. Display force from force-plate tests. */
export function fmtForce(n: number | null | undefined): string | null {
  if (n == null) return null;
  return `${Math.round(n * N_TO_LBF)} lbf`;
}

export function fmtPct(pct: number | null | undefined): string | null {
  if (pct == null) return null;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

export function fmtFloat(n: number | null | undefined, digits = 2): string | null {
  if (n == null) return null;
  return n.toFixed(digits);
}

/** m/s → mph. */
export function fmtSpeed(ms: number | null | undefined): string | null {
  if (ms == null) return null;
  return `${(ms * MS_TO_MPH).toFixed(1)} mph`;
}

/** meters → yards (used for sprint distance, short distances). */
export function fmtYd(m: number | null | undefined): string | null {
  if (m == null || m <= 0) return null;
  return `${Math.round(m * M_TO_YD)} yd`;
}

/** meters → feet (for very short distances). */
export function fmtFt(m: number | null | undefined): string | null {
  if (m == null || m <= 0) return null;
  return `${Math.round(m * M_TO_FT)} ft`;
}

const KM_TO_MI_FACTOR = 1.6093445;

/**
 * Seconds-per-kilometer → "M:SS /mi" display string.
 * 240 s/km × 1.6093 ≈ 386 s/mi ≈ 6:26 /mi.
 */
export function fmtPaceMi(s_per_km: number | null | undefined): string | null {
  if (s_per_km == null || s_per_km <= 0) return null;
  const s_per_mi = s_per_km * KM_TO_MI_FACTOR;
  const m = Math.floor(s_per_mi / 60);
  const s = Math.round(s_per_mi % 60);
  return `${m}:${s < 10 ? '0' : ''}${s} /mi`;
}
